import React, { useEffect, useRef, useState } from 'react';
import { Stethoscope, Copy, X, Download, Upload, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { lirePatientDoctipro, effacerPatientDoctipro, abonnerPasserelle, repondreAccuse, lireTexteDoctipro } from '../../shared/passerelleDoctipro';
import { useExtensionDoctipro, appelerExtension } from '../../shared/extensionDoctipro';
import { recupererPatientDoctipro, envoyerTexteDoctipro } from '../../shared/doctiproActions';
import { copierTexte } from '../accueil/copiePatient';

export const usePatientDoctipro = () => {
  const [patient, setPatient] = useState(() => lirePatientDoctipro());
  useEffect(() => abonnerPasserelle((m) => {
    if (m.type === 'patient' || m.type === 'storage') { setPatient(lirePatientDoctipro()); if (m.type === 'patient') repondreAccuse(); }
  }), []);
  return patient;
};

export const nomPatientDoctipro = (p) => [p?.infos?.nom, p?.infos?.prenom].filter(Boolean).join(' ') || 'Patient Doctipro';

/* Bouton flottant (extension installée) : récupérer le patient / coller le texte préparé dans l'onglet Doctipro. */
const BoutonDoctipro = () => {
  const [ouvert, setOuvert] = useState(false);
  const [occupe, setOccupe] = useState('');
  const [prepare, setPrepare] = useState(() => lireTexteDoctipro());
  useEffect(() => abonnerPasserelle(() => setPrepare(lireTexteDoctipro())), []);

  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const f = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', f);
    return () => document.removeEventListener('mousedown', f);
  }, [ouvert]);
  const lancer = async (cle, fn) => { setOccupe(cle); try { await fn(); } finally { setOccupe(''); setOuvert(false); } };
  return (
    <div className="relative" ref={ref}>
      {ouvert && (
        <div className="absolute top-full left-0 mt-2 z-50 w-[280px] rounded-2xl shadow-2xl border border-gray-200 bg-white p-2 space-y-1 animate-in fade-in slide-in-from-top-2" data-testid="doctipro-menu">
          <button onClick={() => lancer('patient', recupererPatientDoctipro)} disabled={!!occupe}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-sm font-semibold text-[#0B1F3A] hover:bg-sky-50 disabled:opacity-50" data-testid="doctipro-recuperer-patient">
            {occupe === 'patient' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-sky-700" />} Récupérer le patient Doctipro
          </button>
          <button onClick={() => lancer('texte', () => envoyerTexteDoctipro(prepare?.texte || '', prepare?.titre || 'Texte'))} disabled={!!occupe || !prepare?.texte}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-sm font-semibold text-[#0B1F3A] hover:bg-sky-50 disabled:opacity-50" data-testid="doctipro-coller-texte">
            {occupe === 'texte' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-emerald-700" />}
            <span className="min-w-0 truncate">{prepare?.texte ? `Coller « ${prepare.titre || 'Texte'} » dans Doctipro` : 'Aucun texte préparé'}</span>
          </button>
          <button onClick={() => lancer('voir', async () => { const r = await appelerExtension('afficherDoctipro'); if (!r.ok) toast.error(r.erreur); })} disabled={!!occupe}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50" data-testid="doctipro-afficher">
            <ExternalLink className="h-4 w-4" /> Afficher l'onglet Doctipro
          </button>
        </div>
      )}
      <button onClick={() => setOuvert(o => !o)} title="Doctipro (extension)"
        className="inline-flex items-center gap-2 h-9 pl-3 pr-3.5 rounded-lg bg-[#0B1F3A] text-white text-xs font-bold shadow-sm hover:bg-[#143058] transition-colors" data-testid="doctipro-bouton">
        <Stethoscope className="h-4 w-4 text-sky-300" /> Doctipro
      </button>
    </div>
  );
};

/* Barre du haut : bouton Doctipro, affiché seulement si l'extension est installée. */
export const BoutonDoctiproNav = () => (useExtensionDoctipro() ? <BoutonDoctipro /> : null);

/* Coin bas-gauche : carte du patient reçu. */
export const DoctiproDock = () => (
  <div className="fixed bottom-4 left-4 z-40 flex flex-col items-start gap-2" data-testid="doctipro-dock">
    <PatientDoctiproCard />
  </div>
);

/* Carte flottante : patient reçu depuis Doctipro (favori), prêt à être utilisé dans les formulaires. */
export const PatientDoctiproCard = () => {
  const patient = usePatientDoctipro();
  if (!patient) return null;
  const heure = new Date(patient.recu).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="max-w-xs w-[300px] rounded-2xl shadow-2xl border border-sky-200 bg-white/95 backdrop-blur p-3 animate-in slide-in-from-bottom-4" data-testid="patient-doctipro-card">
      <div className="flex items-start gap-2">
        <div className="h-9 w-9 rounded-xl bg-sky-100 flex items-center justify-center shrink-0"><Stethoscope className="h-4.5 w-4.5 text-sky-700" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wide font-bold text-sky-700 truncate" title={patient.source || ''}>Patient Doctipro · {heure}{patient.source ? ` · ${patient.source.replace(/^www\./, '')}` : ''}</p>
          <p className="text-sm font-extrabold text-[#0B1F3A] truncate" data-testid="patient-doctipro-nom">{nomPatientDoctipro(patient)}</p>
          {patient.infos?.date_naissance_fr && <p className="text-xs text-gray-500">Né(e) le {patient.infos.date_naissance_fr}{patient.infos.matricule ? ` · ${patient.infos.matricule}` : ''}</p>}
        </div>
        <button onClick={effacerPatientDoctipro} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400" title="Ignorer" data-testid="patient-doctipro-fermer"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={async () => { await copierTexte(patient.texte); toast.success('Fiche patient copiée'); }}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1173D4] text-white text-xs font-bold hover:bg-[#0d5cab] transition-colors" data-testid="patient-doctipro-copier">
          <Copy className="h-3.5 w-3.5" /> Copier la fiche
        </button>
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5">Les formulaires « Coller infos patient » proposent ce patient en un clic.</p>
    </div>
  );
};

export default PatientDoctiproCard;
